import Phaser from "phaser";
import { GAME } from "../config";
import { addFullscreenButton } from "../ui/Fullscreen";

interface MoveRow {
  name: string;
  input: string;
  damage: string;
  note: string;
}

const BASIC: MoveRow[] = [
  { name: "Jab",            input: "LP",        damage: "6",  note: "быстрый прямой" },
  { name: "Стойкий удар",   input: "→ + LP",    damage: "9",  note: "после шага вперёд" },
  { name: "Тяжёлый прямой", input: "HP",        damage: "14", note: "сильный удар рукой" },
  { name: "Апперкот",       input: "↓ + HP",    damage: "18", note: "knockup, антиэйр" },
  { name: "Front kick",     input: "LK",        damage: "7",  note: "быстрая нога" },
  { name: "Раунд-кик",      input: "HK",        damage: "12", note: "дальний кик" },
  { name: "Подсечка",       input: "↓ + HK",    damage: "10", note: "knockdown" },
  { name: "Прыжок-кик",     input: "↑ + LK/HK", damage: "11", note: "удар в воздухе" },
  { name: "Бросок",         input: "LP+LK",     damage: "16", note: "только вблизи" },
  { name: "Блок",           input: "BLK",       damage: "—",  note: "−70% урона" },
  { name: "Спринт",         input: "RUN держать","damage": "—", note: "ускорение в 1.6×" },
];

const ARCHETYPES_TEXT: { name: string; color: string; tagline: string; specs: string[] }[] = [
  {
    name: "ЛЕО — БАЛАНС", color: "#4499ff",
    tagline: "Универсал. HP=100, скорость=250, прыжок=700.",
    specs: [
      "Молниеносный выпад  →↓→ + LP    (дэш сквозь врага)",
      "Двойной апперкот    ↓ держать, ↑ + HP   (заряженный)",
      "Удар Дракона (super) →↓→ + HP+HK   (3-серия)",
    ],
  },
  {
    name: "МАЙК — СКОРОСТЬ", color: "#ff8800",
    tagline: "Лёгкий. HP=85, скорость=320, прыжок=750.",
    specs: [
      "Тройной jab          LP-LP-LP подряд",
      "Парирующий перехват  → + BLK в момент удара",
      "Вихрь (super)        →↓→ + LK+HK",
    ],
  },
  {
    name: "ДОН — ТЕХНИКА", color: "#9944dd",
    tagline: "Контратака. HP=95, скорость=220, прыжок=680.",
    specs: [
      "Энергетический выпад →↓→ + HP   (проджектайл)",
      "Контратака стойки    ↓ + LP+LK   (перехват)",
      "Стальной шквал (super) ↓→↓ + HP",
    ],
  },
  {
    name: "РАФ — МОЩЬ", color: "#ff4444",
    tagline: "Силач. HP=115, скорость=200, прыжок=650.",
    specs: [
      "Бросок через бедро   →↓→ + LP+LK   (command-throw)",
      "Землетрясение        ↓↑ + HK   (slam с воздуха)",
      "Бычий таран (super)  ↓→ + LP+HP",
    ],
  },
];

/**
 * Сцена «Приёмы» — справка по управлению и move-list.
 * Доступна из главного меню. Показывает 6-кнопочную раскладку, базовые удары,
 * 4 архетипа и их спецприёмы (как ориентир — спецприёмы будут постепенно реализованы).
 */
export class MoveListScene extends Phaser.Scene {
  private tab: "basic" | "specs" = "basic";
  private content: Phaser.GameObjects.GameObject[] = [];

  constructor() { super("MoveList"); }

  create() {
    const { WIDTH, HEIGHT } = GAME;
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 28, "📜 ПРИЁМЫ", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    const back = this.add.text(60, 28, "← НАЗАД", {
      fontFamily: "Arial Black", fontSize: "14px",
      color: "#ffffff", backgroundColor: "#444444",
      padding: { x: 12, y: 6 } as any,
    } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    back.on("pointerdown", () => this.scene.start("MainMenu"));
    this.input.keyboard!.on("keydown-ESC", () => this.scene.start("MainMenu"));

    addFullscreenButton(this);

    // Tabs
    const tabBasic = this.add.rectangle(WIDTH / 2 - 80, 60, 150, 32, 0x2a4a8a)
      .setStrokeStyle(2, 0xffd700).setInteractive({ useHandCursor: true });
    this.add.text(WIDTH / 2 - 80, 60, "БАЗА", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0.5);
    const tabSpec = this.add.rectangle(WIDTH / 2 + 80, 60, 150, 32, 0x6a4a2a)
      .setStrokeStyle(2, 0xffd700).setInteractive({ useHandCursor: true });
    this.add.text(WIDTH / 2 + 80, 60, "АРХЕТИПЫ", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0.5);
    tabBasic.on("pointerdown", () => { this.tab = "basic"; this.render(); });
    tabSpec.on("pointerdown", () => { this.tab = "specs"; this.render(); });

    this.render();
  }

  private render() {
    for (const c of this.content) c.destroy();
    this.content = [];

    if (this.tab === "basic") this.renderBasic();
    else this.renderSpecs();
  }

  private renderBasic() {
    const { WIDTH } = GAME;
    const x0 = 80;
    let y = 100;

    const header = this.add.text(WIDTH / 2, y, "6-КНОПОЧНАЯ СХЕМА: LP / HP / LK / HK / BLK / RUN", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#ffd700",
    }).setOrigin(0.5);
    this.content.push(header);
    y += 26;

    const colName = x0;
    const colInput = x0 + 230;
    const colDmg = x0 + 460;
    const colNote = x0 + 540;

    const titles = ["ПРИЁМ", "ИНПУТ", "DMG", "ЗАМЕТКА"];
    titles.forEach((t, i) => {
      const x = [colName, colInput, colDmg, colNote][i];
      const tt = this.add.text(x, y, t, {
        fontFamily: "Arial Black", fontSize: "12px", color: "#aaaaaa",
      });
      this.content.push(tt);
    });
    y += 22;

    BASIC.forEach((m, i) => {
      const fill = i % 2 === 0 ? 0x1a0808 : 0x0a0404;
      const row = this.add.rectangle(WIDTH / 2, y + 11, WIDTH - 60, 24, fill)
        .setStrokeStyle(1, 0x3a1a1a, 0.4);
      this.content.push(row);
      const t1 = this.add.text(colName,  y, m.name,   { fontFamily: "Arial Black", fontSize: "13px", color: "#ffffff" });
      const t2 = this.add.text(colInput, y, m.input,  { fontFamily: "Arial Black", fontSize: "13px", color: "#ffd700" });
      const t3 = this.add.text(colDmg,   y, m.damage, { fontFamily: "Arial Black", fontSize: "13px", color: "#ff8888" });
      const t4 = this.add.text(colNote,  y, m.note,   { fontFamily: "Arial",       fontSize: "12px", color: "#aaaaaa" });
      this.content.push(t1, t2, t3, t4);
      y += 24;
    });

    const hint = this.add.text(WIDTH / 2, y + 16,
      "Тач: 4 кнопки атак справа + BLK + RUN снизу. Клавиатура P1: WASD + F/G/V/B + H + T",
      { fontFamily: "Arial Black", fontSize: "11px", color: "#666666" }
    ).setOrigin(0.5);
    this.content.push(hint);
  }

  private renderSpecs() {
    const { WIDTH } = GAME;
    let y = 90;
    const intro = this.add.text(WIDTH / 2, y, "Спецприёмы зависят от архетипа бойца. Текущий статус — в разработке.",
      { fontFamily: "Arial Black", fontSize: "12px", color: "#aaaaaa" }).setOrigin(0.5);
    this.content.push(intro);
    y += 24;

    const colW = (WIDTH - 80) / 2;
    const colH = 200;
    ARCHETYPES_TEXT.forEach((a, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const cx = 40 + col * colW + colW / 2;
      const cy = y + row * (colH + 8) + colH / 2;

      const card = this.add.rectangle(cx, cy, colW - 10, colH, 0x1a0505)
        .setStrokeStyle(2, parseInt(a.color.replace("#", ""), 16));
      this.content.push(card);

      const title = this.add.text(cx, cy - colH / 2 + 14, a.name, {
        fontFamily: "Arial Black", fontSize: "16px", color: a.color,
        stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);
      const tag = this.add.text(cx, cy - colH / 2 + 36, a.tagline, {
        fontFamily: "Arial", fontSize: "11px", color: "#aaaaaa",
      }).setOrigin(0.5);
      this.content.push(title, tag);

      a.specs.forEach((s, si) => {
        const txt = this.add.text(cx - colW / 2 + 14, cy - colH / 2 + 60 + si * 30, s, {
          fontFamily: "Arial Black", fontSize: "11px", color: "#ffffff",
        });
        this.content.push(txt);
      });
    });
  }
}
